import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ApiError } from "../../../api/client";
import {
  downloadPlayerReport,
  getPlayerReport,
  reportQuery,
  type PlayerReport,
} from "../../../api/playerReports";
import { ReportOverview } from "./ReportOverview";
import { PlayerProfileSection } from "./PlayerProfileSection";
import { PracticeEvidence } from "./PracticeEvidence";
import { WorkPlan } from "./WorkPlan";
import { SessionReflection } from "./SessionReflection";
import { ReportChart } from "./ReportChart";
import { TrialPhysiology } from "./TrialPhysiology";
import { PerformanceSummary } from "./PerformanceSummary";
import { TrialTimeline } from "./TrialTimeline";

const fmt = (value: number | null, unit = "") =>
  value === null
    ? "Sin dato"
    : `${value.toLocaleString("es-AR", { maximumFractionDigits: 1 })}${unit}`;
const date = (value: string | null) =>
  value
    ? new Date(value).toLocaleString("es-AR", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Sin fecha";
const sessionUrl = (id: string) =>
  `/data/reports/sessions/${encodeURIComponent(id)}`;

export function PlayerReportPage() {
  const { sessionId, trialId } = useParams();
  const [params, setParams] = useSearchParams();
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const query = reportQuery(sessionId, trialId, from, to);
  const [section, setSection] = useState("practica");
  const [data, setData] = useState<PlayerReport | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    setExportError("");
    setSection("practica");
    getPlayerReport(query)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e) => {
        if (active)
          setError(
            e instanceof ApiError && e.status === 404
              ? "Este informe no está disponible para tu cuenta."
              : e instanceof Error
                ? e.message
                : "No pudimos cargar el informe.",
          );
      });
    return () => {
      active = false;
    };
  }, [query, retry]);
  async function exportPdf() {
    if (!data) return;
    setExporting(true);
    setExportError("");
    try {
      await downloadPlayerReport(query, data.scope);
    } catch (e) {
      setExportError(
        e instanceof Error ? e.message : "No pudimos descargar el PDF.",
      );
    } finally {
      setExporting(false);
    }
  }
  const c = data?.coaching;
  const physioCharts =
    data?.charts.filter(
      (chart) =>
        /^(hr|variability)/.test(chart.id) &&
        chart.series.some(
          (s) =>
            s.values.filter((v) => v !== null).length >=
            (data.scope === "global" ? 2 : 1),
        ),
    ) ?? [];
  const evolutionCharts =
    data?.charts.filter(
      (chart) =>
        chart.id.startsWith("goals-") &&
        chart.series.some(
          (s) => s.values.filter((v) => v !== null).length >= 2,
        ),
    ) ?? [];
  const current = data?.sessions[0];
  return (
    <div className="player-report">
      <nav aria-label="Ruta del informe" className="report-breadcrumb">
        <Link to="/data">Mis datos</Link>
        <span>/</span>
        <Link to="/data/reports">Mi informe global</Link>
        {sessionId && (
          <>
            <span>/</span>
            <Link to={sessionUrl(sessionId)}>Sesión</Link>
          </>
        )}
        {trialId && (
          <>
            <span>/</span>
            <span>Jugada</span>
          </>
        )}
      </nav>
      <header className="report-header">
        <div>
          <p className="report-eyebrow">Informe personal</p>
          <h1>
            {data?.scope === "global"
              ? "Tu entrenamiento"
              : data?.scope === "trial"
                ? data.title
                : "Tu sesión"}
          </h1>
          <p className="report-byline">
            {data?.player || "Preparando tu informe"}
            {sessionId && current ? ` · ${date(current.date)}` : ""}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={exportPdf}
          aria-label="Descargar PDF"
          disabled={!data || exporting}
        >
          {exporting ? (
            "Generando PDF…"
          ) : (
            <>
              <span className="report-export-label">Descargar </span>PDF
            </>
          )}
        </button>
      </header>
      {!sessionId && (
        <details className="report-filter" open={!!(from || to)}>
          <summary>
            {from || to ? "Período seleccionado" : "Todo tu historial"}{" "}
            <span>Cambiar fechas</span>
          </summary>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const values = new FormData(e.currentTarget);
              setParams({
                from: String(values.get("from") ?? ""),
                to: String(values.get("to") ?? ""),
              });
            }}
          >
            <label>
              Desde
              <input
                className="input"
                type="date"
                name="from"
                defaultValue={from}
                key={`from-${from}`}
              />
            </label>
            <label>
              Hasta
              <input
                className="input"
                type="date"
                name="to"
                defaultValue={to}
                key={`to-${to}`}
              />
            </label>
            <button className="btn" type="submit">
              Aplicar fechas
            </button>
            <button className="btn" type="button" onClick={() => setParams({})}>
              Todo el historial
            </button>
            <p className="report-caption w-full">
              Fechas en UTC. Hasta 50 sesiones por consulta.
            </p>
          </form>
        </details>
      )}
      {exportError && (
        <p role="alert" className="report-error">
          {exportError}
        </p>
      )}
      {error ? (
        <div role="alert" className="report-panel">
          <p>{error}</p>
          <button className="btn" onClick={() => setRetry((n) => n + 1)}>
            Volver a intentar
          </button>
        </div>
      ) : !data || !c ? (
        <p role="status" className="report-panel">
          Preparando tu informe…
        </p>
      ) : (
        <>
          {data.truncated && (
            <p className="report-caption">
              Mostrando las últimas {data.sessions.length} de{" "}
              {data.total_sessions} sesiones. Acotá el período para ver las
              anteriores.
            </p>
          )}
          <ReportOverview data={data} onFocus={() => setSection("trabajo")} />
          <nav
            className="report-section-nav"
            role="tablist"
            aria-label="Secciones del informe"
          >
            {[
              ["perfil", "01", "Tus recursos"],
              ["practica", "02", "Tu práctica"],
              ["trabajo", "03", "Tu próximo foco"],
            ].map(([id, number, label], index) => (
              <button
                key={id}
                id={`tab-${id}`}
                role="tab"
                aria-selected={section === id}
                aria-controls={`panel-${id}`}
                tabIndex={section === id ? 0 : -1}
                onKeyDown={(e) => {
                  const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
                  if (!keys.includes(e.key)) return;
                  e.preventDefault();
                  const next =
                    e.key === "Home"
                      ? 0
                      : e.key === "End"
                        ? 2
                        : (index + (e.key === "ArrowRight" ? 1 : 2)) % 3;
                  const target =
                    e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                      '[role="tab"]',
                    )[next];
                  target?.click();
                  target?.focus();
                }}
                onClick={() => setSection(id!)}
              >
                <span>{number}</span> {label}
              </button>
            ))}
          </nav>
          <div
            id="panel-perfil"
            role="tabpanel"
            aria-labelledby="tab-perfil"
            hidden={section !== "perfil"}
          >
            <PlayerProfileSection data={data} />
          </div>
          <div
            id="panel-practica"
            role="tabpanel"
            aria-labelledby="tab-practica"
            hidden={section !== "practica"}
          >
            <section
              id="practica"
              className="report-section"
              aria-labelledby="practice-heading"
            >
              <h2 id="practice-heading">Qué observamos en tu práctica</h2>
              {data.scope === "trial" && (
                <div className="report-trial-summary">
                  <h3>{c.headline}</h3>
                  <p>{c.summary}</p>
                </div>
              )}
              {data.performance.length > 0 && data.scope !== "trial" && (
                <section
                  className="report-section"
                  aria-labelledby="performance-heading"
                >
                  <div className="report-section-heading">
                    <div>
                      <h2 id="performance-heading">
                        Cómo resolviste cada ejercicio
                      </h2>
                    </div>
                  </div>
                  <PerformanceSummary performance={data.performance} />
                  <p className="report-caption">
                    {c.sample_note}{" "}
                    {data.performance.some((p) =>
                      ["passing", "pases"].includes(p.scenario),
                    ) &&
                      "En pases, esperar cuando corresponde también cuenta como acierto."}
                  </p>
                </section>
              )}
              <PracticeEvidence data={data} />
              {data.trials.length > 0 && (
                <TrialTimeline
                  trials={data.trials}
                  single={data.scope === "trial"}
                />
              )}
              {data.scope === "global" && (
                <section
                  className="report-section"
                  aria-labelledby="sessions-heading"
                >
                  <div className="report-section-heading">
                    <div>
                      <h2 id="sessions-heading">Tus sesiones, una por una</h2>
                    </div>
                    <p className="report-caption">Las más recientes primero.</p>
                  </div>
                  {!data.sessions.length && (
                    <div className="report-panel">
                      <p>Todavía no hay sesiones en este período.</p>
                      <p className="report-muted">
                        Después de entrenar, sincronizá la sesión para verla
                        acá.
                      </p>
                    </div>
                  )}
                  <div className="report-sessions">
                    {[...data.sessions].reverse().map((s) => (
                      <article key={s.id} className="report-session-row">
                        <div>
                          <h3>
                            <Link to={sessionUrl(s.id)}>{date(s.date)}</Link>
                          </h3>
                          <p className="report-caption">
                            {s.status} · {fmt(s.duration_min, " min")}{" "}
                            registradas · {s.trials.length}{" "}
                            {s.trials.length === 1 ? "jugada" : "jugadas"}
                          </p>
                          <details className="report-row-context">
                            <summary>Contexto y experiencia</summary>
                            <p className="report-caption">
                              Sueño: {fmt(s.sleep_hours, " h")} · Cafeína:{" "}
                              {s.caffeine === null
                                ? "Sin dato"
                                : s.caffeine
                                  ? "Sí"
                                  : "No"}{" "}
                              · Desde entrenamiento:{" "}
                              {fmt(s.hours_since_training, " h")}
                            </p>
                            {s.reflection && (
                              <SessionReflection
                                session={s}
                                onSaved={() => setRetry((n) => n + 1)}
                              />
                            )}
                          </details>
                        </div>
                        <div className="report-session-results">
                          {s.performance
                            .filter((p) => p.success_rate !== null)
                            .map((p) => (
                              <span key={p.scenario}>
                                <strong>
                                  {p.successes}/{p.resolved}
                                </strong>{" "}
                                {p.label.toLowerCase()}
                              </span>
                            ))}
                          {!s.trials.length && (
                            <span>Sin jugadas para analizar</span>
                          )}
                        </div>
                        <Link
                          className="report-review-link"
                          to={sessionUrl(s.id)}
                        >
                          Ver sesión →
                        </Link>
                      </article>
                    ))}
                  </div>
                  <p className="report-caption">
                    La duración incluye pausas. Un registro largo no significa
                    más tiempo de práctica ni más atención.
                  </p>
                  {evolutionCharts.length > 0 && (
                    <details className="report-disclosure">
                      <summary>Ver resultados entre sesiones</summary>
                      <p className="report-muted">
                        Leé cada punto junto con la cantidad de intentos. Un
                        cambio de ejercicio, nivel o tiempo disponible cambia la
                        comparación.
                      </p>
                      <div className="report-chart-grid">
                        {evolutionCharts.map((chart) => (
                          <ReportChart key={chart.id} chart={chart} />
                        ))}
                      </div>
                    </details>
                  )}
                </section>
              )}
              <section
                className="report-section"
                aria-labelledby="body-heading"
              >
                <div className="report-section-heading">
                  <div>
                    <p className="report-eyebrow">
                      {data.scope === "global"
                        ? "Respuesta del cuerpo"
                        : "Después de la acción"}
                    </p>
                    <h2 id="body-heading">Pulso y recuperación</h2>
                  </div>
                  <span className="report-small-stat">
                    {c.measured} / {data.trial_count} con registro cardíaco
                  </span>
                </div>
                <p className="report-muted report-prose">{c.physiology_note}</p>
                {data.scope === "trial" && data.trials[0] && c.measured > 0 ? (
                  <TrialPhysiology trial={data.trials[0]} />
                ) : physioCharts.length > 0 ? (
                  <div className="report-chart-grid">
                    {physioCharts.map((chart) => (
                      <ReportChart key={chart.id} chart={chart} />
                    ))}
                  </div>
                ) : (
                  c.measured > 0 && (
                    <p className="report-caption">
                      Hay señal en algunas jugadas, pero todavía no hay
                      suficientes sesiones comparables para una lectura global.
                      Podés abrir cada sesión para ver su registro.
                    </p>
                  )
                )}
                {data.scope === "session" &&
                  data.trials.some(
                    (t) => t.hr_peak !== null || t.hr_recovery !== null,
                  ) && (
                    <p className="report-caption">
                      Pico y fin de recuperación son dos momentos del ciclo. La
                      línea entre jugadas no representa una grabación continua
                      del pulso.
                    </p>
                  )}
              </section>
              {data.scope !== "global" &&
                data.sessions.map((s) => (
                  <div key={s.id} className="report-session-context">
                    <>
                      <h3>Cómo llegaste a la sesión</h3>
                      <p className="report-caption">
                        Sueño: {fmt(s.sleep_hours, " h")} · Cafeína en las 2 h
                        previas:{" "}
                        {s.caffeine === null
                          ? "Sin dato"
                          : s.caffeine
                            ? "Sí"
                            : "No"}{" "}
                        · Desde el último entrenamiento físico:{" "}
                        {fmt(s.hours_since_training, " h")}. Datos de contexto;
                        no determinan una nota de preparación.
                      </p>
                      <SessionReflection
                        key={s.id}
                        session={s}
                        onSaved={() => setRetry((n) => n + 1)}
                      />
                    </>
                  </div>
                ))}
              <div className="report-panel">
                <h3>Tu recorrido en el protocolo</h3>
                <p>
                  {data.sessions.length}{" "}
                  {data.sessions.length === 1 ? "sesión" : "sesiones"} en este
                  informe · {data.trial_count} jugadas registradas.
                </p>
                <p className="report-caption">
                  {data.protocol.ended_at
                    ? `La intervención finalizó el ${date(data.protocol.ended_at)}.`
                    : data.protocol.active
                      ? "El programa asignado no tiene un cierre registrado."
                      : "No hay un seguimiento de protocolo asignado a esta cuenta."}{" "}
                  {data.protocol.note}
                </p>
              </div>
            </section>
          </div>
          <div
            id="panel-trabajo"
            role="tabpanel"
            aria-labelledby="tab-trabajo"
            hidden={section !== "trabajo"}
          >
            <WorkPlan data={data} />
          </div>
          <details className="report-disclosure report-method">
            <summary>Contexto y alcance de los datos</summary>
            <p className="report-caption">{data.reading.method}</p>
            {data.notes.map((note) => (
              <p className="report-caption" key={note}>
                {note}
              </p>
            ))}
            <p className="report-caption">
              Las comparaciones fisiológicas globales requieren sesiones
              finalizadas de al menos 10 minutos, referencia inicial explícita y
              reloj estable. La referencia incluye práctica, no reposo. HR es
              frecuencia cardíaca; RMSSD describe variabilidad entre latidos.
            </p>
            {data.sessions.map((s) => (
              <p key={s.id} className="report-caption break-all">
                {date(s.date)} · {s.protocol || "Protocolo no registrado"} ·{" "}
                {s.id}
                {s.warning ? ` · ${s.warning}` : ""}
                {data.scope !== "global"
                  ? ` · Sueño: ${fmt(s.sleep_hours, " h")} · Cafeína reciente: ${s.caffeine === null ? "Sin dato" : s.caffeine ? "Sí" : "No"}`
                  : ""}
              </p>
            ))}
            <p className="report-caption">
              Generado {date(data.generated_at)} · {data.version}
            </p>
          </details>
        </>
      )}
    </div>
  );
}
